import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useAuthInfo } from '@propelauth/react';
import analytics from './segmentAnalytics';
import * as Sentry from "@sentry/react";
import { ProductSummaryProvider } from './context/ProductSummaryContext';
import { ModalProvider } from './context/ModalContext';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { PipelineProvider } from './context/PipelineContext';
import logger from './logger';

const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const PersonaDisplay = lazy(() => import('./components/Simulation/PersonaDisplay'));
const SimulationPage = lazy(() => import('./components/Simulation/SimulationPage'));
const CallReviewPage = lazy(() => import('./components/CallReview/CallReviewPage'));
const SimulationHistory = lazy(() => import('./components/Dashboard/SimulationHistory'));
const CheckoutSuccess = lazy(() => import('./components/Dashboard/CheckoutSuccess'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard/AdminDashboard'));
const TeamOverview = lazy(() => import('./components/AdminDashboard/TeamOverview'));
const UserDetail = lazy(() => import('./components/AdminDashboard/UserDetail'));
const PipelineView = lazy(() => import('./components/Pipeline/PipelineView'));
const AccountView = lazy(() => import('./components/Pipeline/AccountView'));
const SiteStats = lazy(() => import('./components/SiteAdmin/SiteStats').then(module => {
  console.log('SiteStats component loaded successfully');
  return module;
}).catch(err => {
  console.error('Error loading SiteStats component:', err);
  throw err;
}));

const LoadingComponent = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    Loading...
  </div>
);

let ExportedApp;
if (process.env.NODE_ENV !== 'development') {
  ExportedApp = Sentry.withProfiler(App);
} else {
  ExportedApp = App;
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#183153',
    },
    secondary: {
      main: '#ff9800',
    },
    background: {
      default: '#f5f5f5',
    },
    link: {
      main: '#2853eb', // your custom link color
    },
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#2853eb', // use your custom link color
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: '#2853eb', // use your custom link color
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    let pageTitle = 'Coachable AI';
    switch (location.pathname) {
      case '/':
        pageTitle = 'Dashboard | Coachable AI';
        break;
      case '/persona-display':
        pageTitle = 'Persona Display | Coachable AI';
        break;
      case '/simulation':
        pageTitle = 'Simulation Page | Coachable AI';
        break;
      case '/simulation-history':
        pageTitle = 'Simulation History | Coachable AI';
        break;
      case '/site-admin/stats':
        pageTitle = 'Site Admin Stats | Coachable AI';
        break;
      default:
        if (location.pathname.startsWith('/call-review')) {
          pageTitle = 'Call Review | Coachable AI';
        }
        break;
    }

    document.title = pageTitle;

    analytics.page({
      title: pageTitle,
      path: location.pathname,
      url: window.location.href,
    });
  }, [location]);

  return null;
}

function AppRoutes() {
  const { user, isAdmin, currentOrg, isSiteAdmin, isLoading } = useAuthContext();
  const orgId = currentOrg ? currentOrg.orgId : null;

  return (
    <PipelineProvider orgId={orgId}>
      <Suspense fallback={<LoadingComponent />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/persona-display" element={<PersonaDisplay />} />
          <Route path="/simulation" element={<SimulationPage />} />
          <Route path="/call-review/:simulationId" element={<CallReviewPage />} />
          <Route path="/simulation-history" element={<SimulationHistory />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route path="/pipeline/*" element={<PipelineView userId={user?.userId} orgId={orgId} />} />
          <Route path="/account/:accountName/:accountPrettyName" element={<AccountView userId={user?.userId} orgId={orgId} />} />
          {isAdmin && (
            <Route path="/admin" element={<AdminDashboard />}>
              <Route index element={<TeamOverview />} />
              <Route path="user/:userId" element={<UserDetail />} />
            </Route>
          )}
          {/* Special handling for site admin route */}
          <Route path="/site-admin/stats" element={
            isLoading ? <LoadingComponent /> : 
            isSiteAdmin ? <SiteStats /> : 
            <Navigate to="/" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </PipelineProvider>
  );
}

function App() {
  useEffect(() => {
    analytics.ready(() => {
      logger.info('Segment initialized');
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <PageTracker />
        <AuthProvider>
          <ModalProvider>
            <ProductSummaryProvider>
              <AppRoutes />
            </ProductSummaryProvider>
          </ModalProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default ExportedApp;
